import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, MessageSquare, Send } from "lucide-react";
import { API, authHeaders } from "./shared";

const SmsBlastModal = ({ onClose }) => {
  const [message, setMessage] = useState("Wednesday Silk Press Special — only $60 this week!");
  const [campaignName, setCampaignName] = useState("");
  const [includeLink, setIncludeLink] = useState(true);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [optin, setOptin] = useState(null);
  const [invSending, setInvSending] = useState(false);
  const inputCls = "w-full bg-brand-bg border border-brand-border text-brand-text font-sans text-sm px-3 py-2 focus:border-brand-gold outline-none";

  useEffect(() => {
    axios.get(`${API}/admin/sms-optin-campaign/preview`, authHeaders())
      .then((r) => setOptin(r.data))
      .catch(() => {});
  }, []);

  const sendOptinInvite = async () => {
    if (!window.confirm(`Send the opt-in invitation text to ${optin.eligible_count} past client${optin.eligible_count === 1 ? "" : "s"}?\n\nThey only join your promo list if they reply YES.`)) return;
    setInvSending(true);
    try {
      const r = await axios.post(`${API}/admin/sms-optin-campaign`, {}, authHeaders());
      toast.success(`Invite sent to ${r.data.sent} past clients — they join only after replying YES`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invites");
    } finally { setInvSending(false); }
  };

  const refreshPreview = useCallback(() => {
    if (!message.trim()) { setPreview(null); return; }
    axios.post(`${API}/admin/sms-blast/preview`, { message, include_booking_link: includeLink }, authHeaders())
      .then((r) => setPreview(r.data))
      .catch(() => {});
  }, [message, includeLink]);

  useEffect(() => {
    const t = setTimeout(refreshPreview, 400);
    return () => clearTimeout(t);
  }, [refreshPreview]);

  const send = async () => {
    if (!preview) { toast.error("Write a message first"); return; }
    if (!window.confirm(`Send this SMS to ${preview.recipients_count} opted-in client${preview.recipients_count === 1 ? "" : "s"} now?\n\n"${preview.final_message}"`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/sms-blast`, { message, include_booking_link: includeLink, name: campaignName || null }, authHeaders());
      toast.success(`SMS sent to ${r.data.sent} of ${r.data.total} opted-in clients`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send SMS blast");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="sms-blast-modal">
      <div className="bg-brand-surface border border-brand-gold/50 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-brand-text flex items-center gap-2">
            <MessageSquare size={16} className="text-brand-gold" /> SMS Blast
          </h3>
          <button data-testid="sms-blast-close" onClick={onClose} className="p-1.5 text-brand-text-muted hover:text-brand-text"><X size={16} /></button>
        </div>
        <p className="font-sans text-xs text-brand-text-muted mb-4">
          Texts ONLY clients who checked "text me exclusive offers" when booking (and haven't replied STOP). Branding, booking link and the STOP opt-out line are added automatically.
        </p>
        <label className="font-sans text-xs text-brand-text-muted">Campaign name (optional)</label>
        <input data-testid="sms-campaign-name-input" className={`${inputCls} mb-3`} value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="e.g. Wednesday Special SMS" />
        <label className="font-sans text-xs text-brand-text-muted">Your short message</label>
        <textarea data-testid="sms-message-input" rows={3} className={inputCls} value={message} onChange={(e) => setMessage(e.target.value)} />
        <label className="flex items-center gap-2 mt-2 cursor-pointer">
          <input data-testid="sms-include-link" type="checkbox" checked={includeLink} onChange={(e) => setIncludeLink(e.target.checked)} className="w-4 h-4 accent-[#D4AF37]" />
          <span className="font-sans text-xs text-brand-text-muted">Include booking link</span>
        </label>

        {preview && (
          <div className="mt-4" data-testid="sms-preview">
            <p className="font-sans text-[10px] uppercase tracking-wider text-brand-text-muted mb-2">Exact text clients receive</p>
            <div className="bg-brand-bg border border-brand-gold/30 rounded-2xl rounded-bl-sm p-3 max-w-[85%]">
              <p data-testid="sms-preview-text" className="font-sans text-sm text-brand-text leading-relaxed">{preview.final_message}</p>
            </div>
            <p className="font-sans text-[11px] text-brand-text-muted mt-2">
              {preview.char_count} characters · {preview.segments} SMS segment{preview.segments === 1 ? "" : "s"} ·{" "}
              <span data-testid="sms-recipients-count" className={preview.recipients_count === 0 ? "text-amber-400" : "text-brand-gold"}>
                {preview.recipients_count} opted-in recipient{preview.recipients_count === 1 ? "" : "s"}
              </span>
            </p>
            {preview.recipients_count === 0 && (
              <p className="font-sans text-[11px] text-amber-400 mt-1">No one has opted in to marketing texts yet — clients opt in via the new checkbox on the booking form.</p>
            )}
          </div>
        )}

        <div className="flex gap-3 mt-5">
          <button data-testid="sms-approve-send-btn" onClick={send} disabled={sending || !preview || preview.recipients_count === 0} className="flex items-center gap-2 px-5 py-2 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors disabled:opacity-50">
            <Send size={13} /> {sending ? "Sending…" : "Approve & Send SMS"}
          </button>
          <button data-testid="sms-cancel-btn" onClick={onClose} className="px-5 py-2 border border-brand-border text-brand-text-muted font-sans text-xs hover:border-brand-gold transition-colors">Cancel</button>
        </div>

        {optin && (
          <div className="mt-6 border-t border-brand-border pt-4" data-testid="optin-campaign-section">
            <p className="font-sans text-xs font-semibold text-brand-text mb-1">Grow your text list — Past Client Opt-In</p>
            <p className="font-sans text-[11px] text-brand-text-muted mb-2">
              Invite <span data-testid="optin-eligible-count" className="text-brand-gold font-semibold">{optin.eligible_count}</span> past client{optin.eligible_count === 1 ? "" : "s"} (not yet opted in, never replied STOP). They join your promo list ONLY if they reply YES.
            </p>
            <div className="bg-brand-bg border border-brand-border p-2.5 mb-2">
              <p className="font-sans text-[11px] text-brand-text-muted leading-relaxed">{optin.message}</p>
            </div>
            <button data-testid="optin-invite-send-btn" onClick={sendOptinInvite} disabled={invSending || optin.eligible_count === 0} className="px-4 py-1.5 border border-brand-gold text-brand-gold font-sans text-xs hover:bg-brand-gold/10 transition-colors disabled:opacity-50">
              {invSending ? "Sending invites…" : "Send Opt-In Invite"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmsBlastModal;
